/**
 * Spec 107 phase D — the CLIENT-SAFE half of lib/mcp-directory.ts: the
 * URL root, the install-client list, the copy object and the pure
 * helpers. No server imports here — `InstallSnippets` is a client
 * component and must never pull the domain package (and through it
 * pg / KMS / google-auth) into the browser bundle. lib/mcp-directory.ts
 * re-exports everything below, so server code keeps one import.
 */
import type { McpDirectoryCapability, McpServerDetail } from '@repo/control-plane-domain';

export const MCP_DIRECTORY_PATH = '/integrations/mcp';

/** Install clients, in the order the switcher shows them (§4.2). */
export const MCP_CLIENTS = [
  { id: 'claude-code', label: 'Claude Code' },
  { id: 'claude-desktop', label: 'Claude Desktop' },
  { id: 'cursor', label: 'Cursor' },
  { id: 'vscode', label: 'VS Code' },
  { id: 'json', label: 'JSON' },
] as const;
export type McpClientId = (typeof MCP_CLIENTS)[number]['id'];

/** The directory's copy — one object the page and the twin both render. */
export const MCP_DIRECTORY = {
  path: MCP_DIRECTORY_PATH,
  overline: 'MCP server directory',
  headingPrefix: 'Every MCP server, with the line to ',
  headingItalic: 'paste',
  paragraph:
    'One catalog, aggregated from the public directories, deduplicated and refreshed daily. Each server shows its current version, the exact setup snippet for your client, what it can do, and whether connect0 already supports the connection it needs.',
  lede: 'Third-party servers only. Our own surface has its own pages.',
  metaTitle: 'MCP server directory — every server, with the line to paste',
  metaDescription:
    'A deduplicated directory of MCP servers with current versions, per-client install snippets, capability summaries, and the connect0 connection each one can use.',
  search: {
    placeholder: 'Search by name…',
    submit: 'Search',
    categoryLabel: 'Category',
    authLabel: 'Authentication',
    anyCategory: 'All categories',
    anyAuth: 'Any auth',
    authOptions: [
      { value: 'none', label: 'No credentials' },
      { value: 'oauth', label: 'OAuth' },
      { value: 'api_key', label: 'API key' },
    ],
    counter: (shown: number, total: number | null) =>
      total === null || total <= shown
        ? `${shown.toLocaleString('en-US')} server${shown === 1 ? '' : 's'}`
        : `${shown.toLocaleString('en-US')} of ${total.toLocaleString('en-US')} servers shown`,
    empty: 'No servers found.',
    next: 'Next page →',
  },
  /**
   * Spec 118 §4 (O1) — the first install tab. Every other tab tells you
   * how to run the server yourself; this one is the only place the page
   * answers "what changes if it runs here", so it goes first rather than
   * last. It is not a snippet client: it is a deep link that carries the
   * slug into a project.
   */
  connect0Tab: {
    id: 'connect0',
    label: 'Use in connect0',
    pitch: 'Run it with its own identity, a budget you set, and every call audited — no local config to keep in sync.',
    cta: 'Add to a connect0 project',
    signedOutNote: 'You will sign in first, then land back on this page.',
    credentialsNote: 'This server needs credentials — you will be asked for them after it is added.',
  },

  /**
   * Spec 118 §6 — the terms our own catalog data carries.
   *
   * Modelled on the attribution-plus-link shape other MCP directories use
   * (Glama's licence is the clearest example). We ask for the same thing, and we spell
   * out the one detail that licence usually leaves implicit — the link has
   * to be a real one. A `nofollow` backlink is not attribution; it is a
   * mention that search engines are told to ignore.
   */
  license: {
    path: `${MCP_DIRECTORY_PATH}/license`,
    overline: 'Data licence',
    heading: 'Use the catalog — say where it came from',
    summary:
      'The directory is free to reuse, including commercially. Two conditions, and they are the whole licence: name connect0 as the source, and link back to the directory with a link search engines and agents can follow.',
    linkLabel: 'Data licence',
    footerNote: 'Reusing this data? Two conditions:',
    terms: [
      {
        title: 'Attribution',
        body: 'Every page, feed, dataset or answer built on this catalog names connect0 as the source, visibly — in the same view as the data, not in a colophon someone has to hunt for.',
      },
      {
        title: 'A followable backlink',
        body: 'That attribution links to the MCP directory, and the link is indexable: no rel="nofollow", no rel="sponsored", no redirect through a blocked path, no meta robots rule that strips it. A link search engines are told to ignore does not count as a link.',
      },
      {
        title: 'Facts stay facts',
        body: 'Do not present the data as endorsed by connect0, and do not alter a version, capability or install snippet and keep our name on it. Correcting or filtering the set is fine; misattributing a changed fact is not.',
      },
      {
        title: 'Third-party marks are not ours to license',
        body: 'Server names, logos and trademarks belong to their owners. This licence covers the catalog we assemble — never the marks inside it.',
      },
    ],
    attributionHeading: 'The line to copy',
    attributionHtml: `<a href="https://connect0.ai${MCP_DIRECTORY_PATH}">MCP server directory</a> data by connect0`,
    attributionMarkdown: `[MCP server directory](https://connect0.ai${MCP_DIRECTORY_PATH}) data by connect0`,
    precedentHeading: 'Why these terms',
    precedentBody:
      'Because we ask nothing we would not accept ourselves. Directories are only worth reading when the work of assembling them is traceable to whoever did it — and a link that search engines and agents can follow is what makes that traceability real.',
  },
  /**
   * Spec 118 §3.1 — the submission control on the directory index.
   *
   * ONE field, the URL: every field we ask for is a field a submitter can
   * get wrong, and discovery works the rest out. Signed-out visitors see
   * the same control and sign in first — the page is public, the write is
   * not (O3: sign-in plus the §3.4 limits plus review is the barrier).
   */
  submit: {
    cta: 'Submit an MCP server',
    title: 'Submit an MCP server',
    description:
      'One field: the URL. We work out the name, the version and what it can do, and a person checks it before it appears here.',
    urlLabel: 'Server URL',
    urlHint: 'A remote endpoint, a repository, or a package page. https only.',
    urlPlaceholder: 'https://example.com/mcp',
    submitLabel: 'Submit for review',
    submitPending: 'Sending…',
    cancel: 'Cancel',
    /** CON-951 — a finished submission is closed, not cancelled: one primary action. */
    close: 'Close',
    signedOutNote: 'Sign in first — you land back here with the form open.',
    /** The query key that opens the dialog on load (`?submit=1`). */
    param: 'submit',
    paramValue: '1',
    outcomes: {
      accepted: 'We’ll take a look — it shows up once reviewed.',
      duplicate: 'That one is already listed.',
      duplicateLinkLabel: 'Open its page',
      // §3.4 — the two limits, each said with the number and the wait, so
      // "try later" is never the whole answer.
      perDay: 'That is 10 submissions in 24 hours, the daily limit. Try again in 24 hours.',
      concurrentPending:
        'Three of your submissions are still waiting for review — the most that can be open at once. Send another once one of them is reviewed.',
      emptyUrl: 'Paste the server’s URL first.',
      failed: 'That did not go through. Try again in a moment.',
    },
  },
  collectionsOverline: 'Curated collections',
  collectionsHeading: 'Hand-picked, with a written reason',
  proof: {
    claim: 'The whole directory answers as markdown — the same facts, no HTML.',
    command: "curl -H 'Accept: text/markdown' https://connect0.ai/integrations/mcp",
    returns: 'Returns the same integrations, with the ways to connect each, as markdown.',
  },
  labels: {
    version: 'Version',
    versionUnknown: 'unknown',
    remote: 'hosted',
    install: 'Install',
    installHint:
      'Generated from the server’s transport data — never hand-written. Placeholders in angle brackets are yours to fill.',
    capabilities: 'What it can do',
    tools: 'Tools',
    resources: 'Resources',
    prompts: 'Prompts',
    noCapabilities: 'No capability summary yet — the publisher has not declared one and it has not been verified.',
    auth: 'Authentication',
    authNone: 'Needs no credentials.',
    authOauth: (provider: string) => `OAuth to ${provider}.`,
    authApiKey: (env: string) => `An API key, passed as ${env}.`,
    connection: 'Connection on connect0',
    connectionSupported: (provider: string) => `connect0 supports ${provider} as a connection.`,
    connectionNone: 'No connect0 connection applies — the server needs no account credential.',
    /** Spec 192-d D1 — the link up to the integration this server is a way to connect. */
    partOfIntegration: (name: string) => `One of the ways to connect ${name}`,
    connectionSetup: 'Set it up on your account',
    why: 'Why you would use it',
    gotchas: 'Setup gotchas',
    facts: 'Facts',
    repo: 'Source',
    website: 'Website',
    license: 'Licence',
    publisher: 'Publisher',
    // Spec 118 §5 — the enrichment block (rendered when the pass has run).
    about: 'About',
    company: 'Company',
    firstParty: 'first-party',
    thirdParty: 'third-party',
    docs: 'Docs',
    pricing: 'Pricing',
    enrichedBy: (model: string) =>
      `Summary written by ${model} from the server’s own site and README; facts above come from the registries.`,
    // Spec 118 §5 — the health family (rendered when the pass reached a source).
    health: 'Project health',
    healthStars: 'GitHub stars',
    healthOpenIssues: 'Open issues',
    healthLastCommit: 'Last commit',
    healthArchived: 'The repository is archived — expect no further releases.',
    healthWeeklyDownloads: 'npm downloads (week)',
    healthLatestPublish: 'Latest npm release',
    healthCredit: (date: string) => `Read from GitHub and npm on ${date}.`,
    registry: 'Official registry id',
    verifiedIdentity: 'Identity last verified',
    verifiedVersions: 'Versions last checked',
    verifiedCapabilities: 'Capabilities last checked',
    collections: 'In collections',
    deprecated: (date: string) =>
      `This server disappeared from every upstream directory on ${date}. The page stays for one quarter so links keep working; after that it answers 410 Gone.`,
    logoCredit: (source: string, license: string | null) =>
      license ? `Logo: ${source} (${license})` : `Logo: ${source}`,
    notIndexed: 'Not indexed by search engines yet — this page is missing:',
    back: '← All MCP servers',
  },
  categoryPage: {
    overline: 'Curated collection',
    serversHeading: 'Servers in this collection',
    empty: 'No servers assigned yet.',
  },
} as const;

export function daysBetween(iso: string, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000));
}

/** §4.3 — the label the page shows next to the capability list.
 *  "Verified 3 days ago" and "As declared by the publisher" are
 *  different claims and are never blurred. */
export function capabilityLabel(caps: readonly McpDirectoryCapability[], now: Date = new Date()): string {
  const verified = caps.filter((c) => c.discovery !== 'declared' && c.verified_at);
  if (verified.length > 0) {
    const latest = verified
      .map((c) => c.verified_at as string)
      .sort()
      .at(-1) as string;
    const days = daysBetween(latest, now);
    const where = verified.some((c) => c.discovery === 'verified_remote') ? '' : ' in the sandbox';
    return days === 0
      ? `Verified today${where}`
      : days === 1
        ? `Verified yesterday${where}`
        : `Verified ${days} days ago${where}`;
  }
  if (caps.length > 0) return 'As declared by the publisher';
  return '';
}

/** §4.4 — the auth shape as a sentence. */
export function authSentence(authRequirement: string): string {
  if (authRequirement === 'none') return MCP_DIRECTORY.labels.authNone;
  const [kind, rest] = authRequirement.split(':', 2);
  if (kind === 'oauth' && rest) return MCP_DIRECTORY.labels.authOauth(rest);
  if (kind === 'api_key' && rest) return MCP_DIRECTORY.labels.authApiKey(rest);
  return authRequirement;
}

export function formatDate(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

const COUNT = new Intl.NumberFormat('en-US');

/**
 * Spec 118 §5 — the health family as label/value pairs, the same list on
 * the page and in the markdown twin. Only what the pass actually read:
 * a GitHub-only row lists no npm lines and vice versa, and a row with
 * nothing readable is `null` upstream so this is never called for it.
 */
export function healthFacts(health: NonNullable<McpServerDetail['health']>): Array<{ label: string; value: string }> {
  const L = MCP_DIRECTORY.labels;
  const out: Array<{ label: string; value: string }> = [];
  if (health.github_stars !== null) out.push({ label: L.healthStars, value: COUNT.format(health.github_stars) });
  if (health.github_open_issues !== null)
    out.push({ label: L.healthOpenIssues, value: COUNT.format(health.github_open_issues) });
  if (health.github_pushed_at) out.push({ label: L.healthLastCommit, value: formatDate(health.github_pushed_at) });
  if (health.npm_weekly_downloads !== null)
    out.push({ label: L.healthWeeklyDownloads, value: COUNT.format(health.npm_weekly_downloads) });
  if (health.npm_latest_version) {
    out.push({
      label: L.healthLatestPublish,
      value: health.npm_last_publish_at
        ? `${health.npm_latest_version} (${formatDate(health.npm_last_publish_at)})`
        : health.npm_latest_version,
    });
  }
  return out;
}

/** The first distribution version worth showing: a resolved package
 *  version if any; `hosted` for remote-only; `unknown` otherwise. */
export function headlineVersion(detail: Pick<McpServerDetail, 'distributions'>): string {
  const resolved = detail.distributions.find((d) => d.version !== 'unknown' && d.version !== 'n/a');
  if (resolved) return resolved.version;
  if (detail.distributions.some((d) => d.kind === 'remote')) return MCP_DIRECTORY.labels.remote;
  return MCP_DIRECTORY.labels.versionUnknown;
}

// ── Spec 118 §3.1 — submission outcomes ──────────────────────────────────
//
// `POST /v1/mcp-submissions` answers with an OUTCOME, not an error: a
// duplicate and a rate limit are ordinary replies the person needs a
// sentence for. Mapping outcome → sentence is pure and lives here, beside
// the copy it selects, so it is unit-testable without a network call and
// the dialog holds no copy of its own.

/** The body `POST /v1/mcp-submissions` returns (camelCase, as the domain writes it). */
export type McpSubmitResult =
  | { outcome: 'accepted'; submissionId?: string }
  | { outcome: 'duplicate'; slug: string; serverId?: string }
  | { outcome: 'rate_limited'; reason: 'per_day' | 'concurrent_pending'; retryAfterHours?: number };

/** What the dialog renders after a submit. `idle` is the pre-submit state. */
export interface SubmitServerState {
  status: 'idle' | 'ok' | 'error';
  message: string;
  /** Set for a duplicate — the page of the server we already hold. */
  href?: string;
  linkLabel?: string;
}

export const SUBMIT_IDLE: SubmitServerState = { status: 'idle', message: '' };

/** Outcome → the sentence the submitter reads (§3.1, §3.4). */
export function submitOutcomeState(result: McpSubmitResult): SubmitServerState {
  const O = MCP_DIRECTORY.submit.outcomes;
  switch (result.outcome) {
    case 'accepted':
      return { status: 'ok', message: O.accepted };
    case 'duplicate':
      return {
        status: 'ok',
        message: O.duplicate,
        href: `${MCP_DIRECTORY_PATH}/${result.slug}`,
        linkLabel: O.duplicateLinkLabel,
      };
    case 'rate_limited':
      return { status: 'error', message: result.reason === 'per_day' ? O.perDay : O.concurrentPending };
    default:
      return { status: 'error', message: O.failed };
  }
}

/**
 * The apps/api error envelope → the same shape. A `ValidationError` from
 * `assertSubmittableUrl` ("Only http and https URLs can be submitted.",
 * "That URL is not reachable from the public internet.") is the sentence
 * the submitter must act on, so it is rendered verbatim rather than
 * flattened into a generic failure.
 */
export function submitErrorState(message?: string | null): SubmitServerState {
  return { status: 'error', message: message?.trim() || MCP_DIRECTORY.submit.outcomes.failed };
}

/** `/integrations/mcp?submit=1` — where sign-in returns a signed-out visitor. */
export function submitReturnPath(): string {
  const S = MCP_DIRECTORY.submit;
  return `${MCP_DIRECTORY_PATH}?${S.param}=${S.paramValue}`;
}
